/* eslint-disable import/no-unresolved */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { StakingContext } from '../../../../contexts/staking/context';
import { currencies, exchange, TIER_LEVEL } from '../../../../utilities/constants';
import {
  formatDecimalNumber,
  formatNumber,
  formatNumberWithComma,
} from '../../../../utilities/formatHelpers';
import { validateNumbers } from '../../../../utilities/formValidation';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { USER_PREFERENCES } from '../../../../utilities/sharedQueries';
import FocusableInputArea from '../../../Accounts/partials/FocusableInputArea';
import { WalletsTypes } from '../../../Card/constants';
import { benefitsTiersBadgeColorsMap, mainStakingCurrency } from '../../constants';
import FeatureMainBodyLayout from '../../partials/FeatureMainBodyLayout';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { STAKING_CREATE } from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import { calculateRewardsHelper } from '../../helpers';
import Badge from '../../../../components/Badge';
import { GET_ACTIVE_STAKING_AMOUNT_WITH_TIERS, GET_STAKING_TIERS } from '../../queries';

const CreateStaking = () => {
  const { t: translate } = useTranslation();

  const { state, updateShowRewardInfo } = useContext(StakingContext);

  const history = useHistory();

  const [isSwap, setIsSwap] = useState(true);

  const stakingWallet = state?.mainStakingWallet;

  const availableBalance =
    stakingWallet.available_balance * currencies[stakingWallet?.coin?.toUpperCase()].conversion;

  const [settings] = useSetting('staking_options', [
    'staking_minimum_amount',
    'staking_durations',
    'staking_percentage_rate',
  ]);

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const fiatCurrency = userPref?.displayCurrency;

  const {
    data: stakingTiers,
    isLoading: isLoadingStakingTiers,
    refetch: refetchStakingTiers,
  } = useQuery(GET_STAKING_TIERS, {
    enabled: false,
  });

  const {
    data: activeStakingAmountTiers,
    isLoading: isLoadingActiveStakingAmountTiers,
    refetch: refetchActiveStakingAmountTiers,
  } = useQuery(GET_ACTIVE_STAKING_AMOUNT_WITH_TIERS, {
    enabled: false,
  });

  const { mutate: createStaking, isLoading: isLoadingCreateStaking } = useMutation(STAKING_CREATE, {
    onSettled: (data, error) => {
      if (!error) {
        showAlert({
          message: translate('create_staking_success_note').replace(
            '__CURRENCY__',
            mainStakingCurrency?.toUpperCase()
          ),
          type: 'success',
        });
        history.push('/dashboard/staking');
      } else {
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  const rate = useRate(mainStakingCurrency, fiatCurrency, true);

  const usdConvertRate = useRate(mainStakingCurrency, currencies.USD.fullName, true);

  const fiatStakingInputSpan = useRef();
  const stakingInputSpan = useRef();
  const progressBarRef = useRef();

  const [minStaking, setMinStaking] = useState(settings?.staking_minimum_amount || 0);
  const [maxStaking, setMaxStaking] = useState(100);
  const [stakingAmount, setStakingAmount] = useState(0);
  const [stakingAmountDecimal, setStakingAmountDecimal] = useState(0);
  const [fiatStakingAmount, setFiatStakingAmount] = useState(
    formatNumber(minStaking * rate, exchange.FIAT_VALUE)
  );
  const [fiatStakingInputWidth, setFiatStakingInputWidth] = useState(0);
  const [stakingInputWidth, setStakingInputWidth] = useState(0);
  const [durationInMonths, setDurationInMonths] = useState(0);
  const [rewards, setRewards] = useState({
    daily: 0,
    monthly: 0,
    annual: 0,
  });

  useEffect(() => {
    if (availableBalance) {
      setMaxStaking(parseFloat(availableBalance));
    }
  }, [availableBalance]);

  const [apr, setApr] = useState(0);

  const durationInDaysDisplayValuesArr = useMemo(() => {
    if (settings && settings?.staking_durations) {
      return settings?.staking_durations?.split(',').map((item, index) => ({
        index,
        value: item,
        short: `${item}M`,
        full: translate('no_months_label').replace('__NO__', item),
      }));
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const stakingBenefitsInfo = useMemo(() => {
    let result = {
      showBenefitsBox: false,
    };

    if (
      !isLoadingStakingTiers &&
      !isLoadingActiveStakingAmountTiers &&
      stakingTiers &&
      activeStakingAmountTiers &&
      stakingAmountDecimal
    ) {
      const activeStakedAmountValueInUsd = parseFloat(
        activeStakingAmountTiers?.activeStakedAmountValueInUsd
      );

      const userEnteredAmountInUsd = stakingAmountDecimal * usdConvertRate;

      const stakingAmountInUsd = activeStakedAmountValueInUsd + userEnteredAmountInUsd;

      for (const level of stakingTiers) {
        if (stakingAmountInUsd >= level?.required_staked_bxx_value_in_usd) {
          result = { showBenefitsBox: true, ...level };
        } else {
          break;
        }
      }
    }

    return result;
  }, [
    stakingTiers,
    activeStakingAmountTiers,
    isLoadingStakingTiers,
    isLoadingActiveStakingAmountTiers,
    stakingAmountDecimal,
    usdConvertRate,
  ]);

  const handleChangeStakingAmount = (event) => {
    const { value: amount, min } = event.target;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(decimalAmount, WalletsTypes.CRYPTO)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setFiatStakingAmount(0);
      setStakingAmountDecimal(0);
      setStakingAmount(0);
    }

    const calcFiat = formatNumber(decimalAmount * rate, exchange.FIAT_VALUE);
    const formattedFiatCalc = formatDecimalNumber(calcFiat);

    setFiatStakingAmount(formattedFiatCalc.formattedAmount);
    setStakingAmount(formattedAmount);
    setStakingAmountDecimal(decimalAmount);
  };

  const handleChangeFiatAmount = (event) => {
    const { value: amount, min } = event?.target || event;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(decimalAmount, WalletsTypes.FIAT)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setFiatStakingAmount(0);
      setStakingAmount(0);
    }

    const calcCrypto = formatNumber(decimalAmount / rate, exchange.CRYPTO_VALUE);
    const formattedCalcCrypto = formatDecimalNumber(calcCrypto);

    setStakingAmount(formattedCalcCrypto.formattedAmount);
    setStakingAmountDecimal(formattedCalcCrypto.decimalAmount);
    setFiatStakingAmount(formattedAmount);
  };

  const handleFocus = (e) => {
    if (!e.target) return;
    e.target.select();
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.add('active');
    }
  };

  const handleBlurAmount = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.remove('active');
    }
  };

  const calculateRewards = () => {
    if (apr > 0 && stakingAmountDecimal > 0) {
      const rewardsRsults = calculateRewardsHelper(apr, stakingAmountDecimal);

      setRewards({
        daily: rewardsRsults.daily,
        monthly: rewardsRsults.monthly,
        annual: rewardsRsults.annual,
      });
    }
  };

  useEffect(() => {
    const charactersLength =
      stakingInputSpan.current.value.length > 1 ? stakingInputSpan.current.value.length : 2;
    setStakingInputWidth(charactersLength);
  }, [stakingAmount]);

  useEffect(() => {
    const charactersLength =
      fiatStakingInputSpan.current.value.length > 1 ? fiatStakingInputSpan.current.value.length : 1;
    // to avoid having empty input, fix length to 1 if text length is less then 1
    setFiatStakingInputWidth(charactersLength);
  }, [fiatStakingAmount]);

  useEffect(() => {
    if (progressBarRef && progressBarRef?.current) {
      const newWidth = ((stakingAmountDecimal - minStaking) * 100) / (maxStaking - minStaking);
      progressBarRef.current.style.width = `${newWidth > 0 ? newWidth : 0}%`;
    }
  }, [stakingAmountDecimal, progressBarRef, maxStaking, minStaking]);

  useEffect(() => {
    if (settings && settings?.staking_minimum_amount) {
      setMinStaking(settings?.staking_minimum_amount);
    }
  }, [settings]);

  useEffect(() => {
    setStakingAmount(minStaking);
    setStakingAmountDecimal(minStaking);
    setFiatStakingAmount(formatNumber(minStaking * rate, exchange.FIAT_VALUE));
  }, [minStaking, rate]);

  useEffect(() => {
    if (settings && settings?.staking_percentage_rate) {
      const stakingPercentageRateArr = settings?.staking_percentage_rate?.split(',');

      setApr(stakingPercentageRateArr[durationInMonths]);
    }
  }, [settings, durationInMonths]);

  useEffect(() => {
    calculateRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakingAmountDecimal, apr]);

  useEffect(() => {
    refetchStakingTiers();
    refetchActiveStakingAmountTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disabledButton =
    stakingAmountDecimal <= 0 ||
    stakingAmountDecimal < minStaking ||
    stakingAmountDecimal > availableBalance ||
    isLoadingCreateStaking;

  const benefitsBox = () => (
    <div className="bg-lighter rounded-extra p-3 mt-3">
      <div className="d-flex flex-column">
        <div className="d-flex flex-row justify-content-start align-items-center">
          <p className="text-f5 text-dark">
            {translate('staking_benefit_box_next_tier_level_label')}
          </p>
          <div
            className={`${
              benefitsTiersBadgeColorsMap[stakingBenefitsInfo?.tier].background
            } px-2 py-1 rounded-2 ms-3`}>
            <p
              className={`text-uppercase text-f6 ${
                benefitsTiersBadgeColorsMap[stakingBenefitsInfo?.tier].text
              }`}
              style={{ fontFamily: 'HM Alpha Mono' }}>
              {stakingBenefitsInfo?.name}
            </p>
          </div>
        </div>
        <p className="text-f6 text-shadow">
          {translate('staking_benefit_box_tier_level_sublabel')}
        </p>
      </div>
      <div className="d-flex flex-row justify-content-start align-items-center mt-4">
        <div className="d-flex flex-column">
          <p className="text-f6 text-dark">{translate('staking_benefit_box_cryptodraft_label')}</p>
          <p className="text-f6 text-shadow">
            {translate('staking_benefit_box_cryptodraft_sublabel').replace(
              '__LTV_INTEREST__',
              stakingBenefitsInfo?.cryptodraft_ltv_interest_free
            )}
          </p>
        </div>
        <div className="d-flex flex-column ms-5">
          <p className="text-f6 text-dark">{translate('staking_benefit_box_stableloan_label')}</p>
          <p className="text-f6 text-shadow">
            {stakingBenefitsInfo?.tier === TIER_LEVEL.FIVE &&
              stakingBenefitsInfo?.stableloan_ltv_interest_free &&
              translate('staking_benefit_box_stableloan_sublabel_tier_5')}

            {stakingBenefitsInfo?.tier !== TIER_LEVEL.FIVE &&
              stakingBenefitsInfo?.stableloan_ltv_interest_free &&
              translate('staking_benefit_box_stableloan_sublabel').replace(
                '__LTV_INTEREST__',
                stakingBenefitsInfo?.stableloan_ltv_interest_free
              )}

            {!stakingBenefitsInfo?.stableloan_ltv_interest_free &&
              translate('staking_no_benefits_label')}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <FeatureMainBodyLayout
      withBack
      onPrevClick={() => history.push('/dashboard/staking')}
      title={translate('create_staking_title').replace(
        '__COIN__',
        mainStakingCurrency.toUpperCase()
      )}>
      <p className="text-f6 text-shadow mt-4 pt-3">
        {`${translate('available_balance_label')}: `}
        <span className="text-dark">{`${formatNumberWithComma(
          formatNumber(availableBalance, exchange.CRYPTO_VALUE)
        )} ${mainStakingCurrency.toUpperCase()}`}</span>
      </p>
      <p className="text-f6 text-shadow mt-4">{translate('create_staking_enter_amount_note')}</p>
      <FocusableInputArea className="mt-3 pt-1">
        <input
          required
          max={!isSwap ? maxStaking : ''}
          min={0}
          type="text"
          className="form-control bg-transparent p-0 text-dark text-center border-0"
          value={isSwap ? fiatStakingAmount : stakingAmount}
          style={{
            fontSize: '1rem',
            width: `${isSwap ? fiatStakingInputWidth : stakingInputWidth + 1}ch`,
            height: '3rem',
          }}
          ref={isSwap ? fiatStakingInputSpan : stakingInputSpan}
          onChange={(e) => (isSwap ? handleChangeFiatAmount(e) : handleChangeStakingAmount(e))}
          placeholder="0 "
          disabled
        />
        <span style={{ fontSize: 'inherit', pointerEvents: 'none' }} className="text-f5 text-dark">
          {isSwap ? fiatCurrency?.toUpperCase() : mainStakingCurrency?.toUpperCase()}
        </span>
        <span
          role="button"
          className="mb-1"
          tabIndex="0"
          onKeyPress={() => setIsSwap((prev) => !prev)}
          onClick={() => setIsSwap((prev) => !prev)}>
          <img src="./assets/images/switch.svg" className="ms-2" alt="switch" />
        </span>
      </FocusableInputArea>
      <FocusableInputArea className="h1 amount-input">
        <input
          required
          max={isSwap ? maxStaking : ''}
          min="0"
          type="text"
          className="form-control bg-transparent text-bold p-0 text-center border-0"
          value={isSwap ? stakingAmount : fiatStakingAmount}
          style={{
            fontSize: 'inherit',
            width: `${(isSwap ? stakingInputWidth : fiatStakingInputWidth) + 1}ch`,
            color: 'inherit',
            height: 'inherit',
            textIndent: stakingInputWidth || fiatStakingInputWidth ? 0 : '34%',
          }}
          ref={isSwap ? stakingInputSpan : fiatStakingInputSpan}
          onChange={(e) => (isSwap ? handleChangeStakingAmount(e) : handleChangeFiatAmount(e))}
          onFocus={handleFocus}
          onBlur={handleBlurAmount}
          placeholder="0  "
        />
        <b
          style={{
            fontSize: 'inherit',
            pointerEvents: 'none',
            color: 'inherit',
          }}>
          {isSwap ? mainStakingCurrency?.toUpperCase() : fiatCurrency?.toUpperCase()}
        </b>
      </FocusableInputArea>
      <div className="mb-4 position-relative">
        <div className="slider-progress">
          <div className="progress">
            <div className="progress-bar" ref={progressBarRef} />
          </div>
          <input
            type="range"
            className="form-range"
            value={stakingAmountDecimal}
            max={maxStaking}
            min={minStaking}
            step="0.0001"
            onChange={(e) => {
              handleChangeStakingAmount(e);
            }}
          />
        </div>
        {/* this lines for implementing edges for the slider */}
        <div
          className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start"
          style={{ top: '15%' }}>
          {Array.from({ length: 7 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 1 || item === 7 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
          {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
            <hr
              key={item}
              className={`progress-edges text-disabled ${
                item === 6 ? 'progress-main-edge' : 'progress-edges'
              }`}
              style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
            />
          ))}
        </div>
        <div className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start">
          <p className="text-f6 text-disabled">
            {`${formatNumberWithComma(formatNumber(minStaking, exchange.FIAT_VALUE))}`}
          </p>
          <p className="text-f6 text-disabled text-uppercase">{`${translate(
            'max_button_label'
          )}`}</p>
        </div>
      </div>

      {stakingBenefitsInfo?.showBenefitsBox && benefitsBox()}

      <p className="text-f6 text-shadow mt-4">{translate('create_staking_period_label')}</p>

      <div className="d-flex flex-row justify-content-between align-items-center mt-4 pt-2 gap-2">
        {durationInDaysDisplayValuesArr.map((item) => (
          <Badge
            key={item.value}
            content={`${item.full}`}
            onClick={() => {
              setDurationInMonths(item.index);
            }}
            rounded="rounded-l"
            className={`bg-light py-1 flex-grow-1 text-white text-f5 cursor-pointer bg-lighter ${
              item.index === durationInMonths ? 'active' : ''
            } px-2 px-md-3 text-nowrap`}
          />
        ))}
      </div>
      <div className="d-flex flex-row mt-4 mb-2 rounded-extra bg-lighter p-3">
        <p className="text-dark text-f6 d-flex align-items-center">
          {translate('create_staking_apr_label')}
        </p>
        <span className="text-right flex-fill">
          <p className="text-dark text-f6">{`${apr} %`}</p>
        </span>
      </div>
      <div className="d-flex flex-row rounded-extra bg-lighter px-3 py-4 mt-1">
        <div className="d-flex flex-column flex-md-row gap-md-2">
          <span className="text-dark text-f6 d-flex align-items-center">
            {translate('create_staking_estimate_label_part1')}
          </span>
          <span className="text-dark text-f6 d-flex align-items-center">
            {translate('create_staking_estimate_label_part2')}
          </span>
        </div>
        <span className="d-flex position-relative flex-row justify-content-end align-items-center flex-fill">
          <p className="text-success text-f6">
            {`${formatNumberWithComma(
              formatNumber(
                durationInDaysDisplayValuesArr
                  ? rewards?.monthly * durationInDaysDisplayValuesArr[durationInMonths]?.value
                  : 0,
                exchange.CRYPTO_VALUE
              )
            )} ${mainStakingCurrency.toUpperCase()}`}
          </p>
          <div className="ms-2 cursor-pointer text-hover d-flex flex-row justify-content-center align-items-center">
            {/* mobile */}
            <i
              aria-label="Mute volume"
              onClick={() => updateShowRewardInfo(true)}
              onKeyDown={() => updateShowRewardInfo(true)}
              role="button"
              tabIndex={-1}
              className="d-block d-md-none icon icon-info-icon text-shadow"
              style={{ fontSize: '1.7rem' }}
            />

            {/* browser */}
            <i
              aria-label="Mute volume"
              data-tooltip-id="staking-reward-info-tooltip"
              role="button"
              tabIndex={-1}
              className="d-none d-md-block icon icon-info-icon text-shadow"
              style={{ fontSize: '1.2rem' }}
            />
          </div>
        </span>
      </div>
      <div className="alert alert-info mb-4 mt-4">
        <i className="icon icon-info-icon" />
        <span className="content">{translate('create_staking_info_note')}</span>
      </div>
      <div className="btn-container bg-transparent">
        <div className="w-100 bg-light">
          <button
            type="button"
            disabled={disabledButton}
            loading={isLoadingCreateStaking ? 'true' : 'false'}
            className="btn py-3 btn-primary w-100"
            onClick={() => {
              createStaking({
                amount: stakingAmountDecimal,
                currency: mainStakingCurrency,
                durationInMonths: durationInDaysDisplayValuesArr[durationInMonths]?.value,
              });
            }}>
            {translate('stake_now_btn_label')}
          </button>
        </div>
      </div>
      <Tooltip
        id="staking-reward-info-tooltip"
        place="bottom-start"
        variant="dark"
        opacity={1}
        className="d-none d-md-block p-4 rounded border-1 custom-tooltip">
        <p className="text-white text-f6">{translate('staking_reward_note')}</p>
      </Tooltip>
    </FeatureMainBodyLayout>
  );
};

export default CreateStaking;
